import { useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { HiX, HiDownload, HiExternalLink } from 'react-icons/hi'
import './CertificateViewer.css'

interface CertificateViewerProps {
  isOpen: boolean
  onClose: () => void
  title: string
  issuer: string
  localFile: string | null   // '/images/certificates/aws-cert.pdf' or image path
  credentialUrl: string      // fallback external URL
  color: string
}

function isImage(path: string) {
  return /\.(jpe?g|png|webp|gif|svg)$/i.test(path)
}
function isPdf(path: string) {
  return /\.pdf$/i.test(path)
}

export default function CertificateViewer({
  isOpen, onClose, title, issuer, localFile, credentialUrl, color,
}: CertificateViewerProps) {

  // Close on Escape key
  const handleKey = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose()
  }, [onClose])

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKey)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleKey)
      document.body.style.overflow = ''
    }
  }, [isOpen, handleKey])

  const hasLocal   = Boolean(localFile)
  const showImage  = hasLocal && isImage(localFile!)
  const showPdf    = hasLocal && isPdf(localFile!)

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="cv-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            className="cv-modal"
            initial={{ opacity: 0, scale: 0.92, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 30 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            role="dialog"
            aria-modal="true"
            aria-label={`Certificate: ${title}`}
          >
            {/* Top accent bar */}
            <div className="cv-accent-bar" style={{ background: color }} />

            {/* Header */}
            <div className="cv-header">
              <div className="cv-header-text">
                <h3 className="cv-title">{title}</h3>
                <p className="cv-issuer" style={{ color }}>{issuer}</p>
              </div>
              <button className="cv-close" onClick={onClose} aria-label="Close">
                <HiX />
              </button>
            </div>

            {/* Content area */}
            <div className="cv-body">
              {/* ── Local IMAGE ──────────────────────────────────────────── */}
              {showImage && (
                <div className="cv-image-wrap">
                  <img
                    src={localFile!}
                    alt={title}
                    className="cv-image"
                    onError={(e) => {
                      (e.currentTarget.parentElement as HTMLElement).innerHTML =
                        '<p class="cv-missing">Image could not be loaded. Check the file path in public/images/certificates/</p>'
                    }}
                  />
                </div>
              )}

              {/* ── Local PDF ────────────────────────────────────────────── */}
              {showPdf && (
                <div className="cv-pdf-wrap">
                  <iframe
                    src={`${localFile!}#toolbar=1&navpanes=0&scrollbar=1`}
                    title={title}
                    className="cv-pdf-frame"
                    onError={() => {/* handled by footer fallback */}}
                  />
                  {/* Mobile fallback — iframes unreliable on phones */}
                  <div className="cv-pdf-mobile-hint">
                    <span>📄</span>
                    <p>PDF preview may not work on all devices.</p>
                    <p>Use the download button below to view the certificate.</p>
                  </div>
                </div>
              )}

              {/* ── No local file — instruct user ───────────────────────── */}
              {!hasLocal && (
                <div className="cv-no-file">
                  <div className="cv-no-file-icon">📂</div>
                  <h4>No local file found</h4>
                  <p>
                    Drop your certificate PDF or image into:<br />
                    <code>public/images/certificates/</code>
                  </p>
                  <p className="cv-no-file-hint">
                    Then set <code>localFile</code> in <code>src/utils/data.ts</code>.
                  </p>
                </div>
              )}
            </div>

            {/* Footer actions */}
            <div className="cv-footer">
              {hasLocal && (
                <a
                  href={localFile!}
                  download
                  className="btn btn-primary cv-action-btn"
                >
                  <HiDownload />
                  Download Certificate
                </a>
              )}

              {/* Always show external link if it's a real URL */}
              {credentialUrl && credentialUrl !== '#' && (
                <a
                  href={credentialUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary cv-action-btn"
                >
                  <HiExternalLink />
                  Verify Online
                </a>
              )}

              <button className="btn btn-ghost cv-action-btn" onClick={onClose}>
                Close
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
